
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Guide } from '@/types/guides';
import { PdfPreview } from './PdfPreview';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GuidePreviewProps {
  guide: Guide;
  onClose: () => void;
}

export function GuidePreview({ guide, onClose }: GuidePreviewProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white relative">
        <Button 
          variant="ghost" 
          className="absolute right-2 top-2 h-8 w-8 p-0" 
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <CardContent className="p-6 pt-12">
          <div className="mb-6">
            {guide.cover_image_url && (
              <img
                src={guide.cover_image_url}
                alt={guide.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
            <h2 className="text-2xl font-semibold text-grandpal-green-dark mb-2">{guide.title}</h2>
            <p className="text-grandpal-gray mb-4">{guide.description}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {guide.categories?.map((category, index) => (
                <span
                  key={index}
                  className="text-xs px-3 py-1 bg-grandpal-green/10 text-grandpal-green-dark rounded-full"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>

          {guide.preview_url ? (
            <div className="my-8">
              <div className="border rounded-lg overflow-hidden">
                <div className="p-4 bg-white">
                  <PdfPreview pdfUrl={guide.preview_url} maxPages={5} />
                </div>
              </div>
            </div>
          ) : (
            <div className="prose prose-green max-w-none">
              <div className="text-grandpal-gray">{guide.content}</div>
            </div>
          )}

          <div className="mt-8 flex justify-between items-center">
            <button 
              onClick={onClose}
              className="text-grandpal-green hover:text-grandpal-green-dark"
            >
              Close Preview
            </button>
            
            {guide.download_url && (
              <a 
                href={guide.download_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-grandpal-green-dark hover:bg-grandpal-green-dark/90 text-white px-4 py-2 rounded-md text-sm font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // We'll let the main download handler take care of this
                  onClose();
                }}
              >
                Download Full Guide
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
